"use client";

import { NextImage } from "@components/NextImage";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { GOAL_CARD_ACTIVATE_BG_SRC } from "@screens/plan/constants/goalCardActivateVisual";

export type GoalCardActivateBackgroundProps = {
  className?: string;
  testId?: string;
};

/** Absolute texture background layer for Goal Card V3 Activate. */
export const GoalCardActivateBackground: React.FC<
  GoalCardActivateBackgroundProps
> = ({ className, testId = "save-goal-card-bg" }) => (
  <div
    className={cn(
      "pointer-events-none absolute inset-0 overflow-hidden",
      className,
    )}
    aria-hidden
    data-testid={testId}
  >
    <NextImage
      src={GOAL_CARD_ACTIVATE_BG_SRC}
      alt=""
      width={360}
      height={200}
      className="h-full w-full object-cover"
    />
  </div>
);

export type GoalCardActivateHeaderProps = {
  statement: string;
  description?: string;
  className?: string;
  /** When false, only background renders (stack back layer). */
  showContent?: boolean;
  /** When false, background must be provided by a parent (e.g. Banner article). */
  includeBackground?: boolean;
  bgTestId?: string;
  iconTestId?: string;
  statementTestId?: string;
  statementClassName?: string;
  contentClassName?: string;
};

/**
 * Goal Card V3 Activate header — texture bg + white goal icon + statement.
 * Figma 96138:332439 / 89002:38624.
 */
export const GoalCardActivateHeader: React.FC<GoalCardActivateHeaderProps> = ({
  statement,
  description,
  className,
  showContent = true,
  includeBackground = true,
  bgTestId,
  iconTestId,
  statementTestId = "save-goal-card-summary",
  statementClassName = "title_xl m-0 text-content_inverse_primary",
  contentClassName = "relative z-[1] flex flex-col gap-5",
}) => (
  <div className={cn("relative overflow-hidden", className)}>
    {includeBackground ? (
      <GoalCardActivateBackground testId={bgTestId} />
    ) : null}
    {showContent ? (
      <div className={contentClassName}>
        {iconTestId ? (
          <span
            data-testid={iconTestId}
            className="inline-flex shrink-0"
            aria-hidden
          >
            <OspIcons
              name="goal"
              size={24}
              className="text-content_inverse_primary"
              color={ospThemes.colors.content_inverse_primary}
            />
          </span>
        ) : (
          <OspIcons
            name="goal"
            size={24}
            className="text-content_inverse_primary"
            color={ospThemes.colors.content_inverse_primary}
          />
        )}
        {statement ? (
          <p className={statementClassName} data-testid={statementTestId}>
            {statement}
          </p>
        ) : null}
        {description ? (
          <p
            className="body_s m-0 text-content_inverse_primary"
            data-testid="save-goal-card-description"
          >
            {description}
          </p>
        ) : null}
      </div>
    ) : null}
  </div>
);
